import { Fragment, type ReactNode } from "react";
import { Download } from "lucide-react";
import { downloadAsset } from "@/lib/assetUtils";
import { cn } from "@/lib/utils";
import { ItemHistoryAvatars } from "@/modules/items/ItemHistoryAvatars";
import { ItemHistoryChangeValues } from "@/modules/items/ItemHistoryChangeValues";
import { ItemHistoryDetailPopover } from "@/modules/items/ItemHistoryDetailPopover";
import type { ItemHistoryActor } from "@/modules/items/itemHistoryLabels";
import { OpportunityActivityMessagePopover } from "./OpportunityActivityMessagePopover";
import type {
  ActivityMessage,
  ActivitySentence,
  ActivitySentencePart,
} from "./opportunityActivitySentences";

const CHIP_CLASSES =
  "-mx-1 inline-flex items-baseline gap-1 rounded-sm px-1 text-left font-semibold hover:bg-muted focus-visible:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted";
const LINK_CLASSES =
  "text-left font-semibold underline decoration-border underline-offset-[3px] hover:decoration-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm";

/** How strongly a line reads: a customer's action stands out, a team view recedes. */
export type ActivityLineEmphasis = "default" | "external" | "muted";

interface OpportunityActivityLineProps {
  actor: ItemHistoryActor;
  sentence: ActivitySentence;
  time: string;
  emphasis: ActivityLineEmphasis;
  message: ActivityMessage | null;
  showActorName: boolean;
  onOpenActor: (() => void) | null;
  onOpenQuote: (quoteId: string) => void;
  trailing?: ReactNode;
}

/**
 * One event as one line, in the item history's grammar: who, what they did,
 * the changed value in its own right-aligned column, and when. A message
 * reads as a one-line preview under the sentence.
 * @param actor - Who acted
 * @param sentence - What they did
 * @param time - When, as h:mm a
 * @param emphasis - Whether the line stands out or recedes
 * @param message - The full message the preview opens, when the event has one
 * @param showActorName - Whether the sentence names the actor
 * @param onOpenActor - Opens the actor's agent thread, when it has one
 * @param onOpenQuote - Opens a quote the sentence names
 * @param trailing - Rendered after the sentence, such as an agent turn's toggle
 */
export const OpportunityActivityLine = ({
  actor,
  sentence,
  time,
  emphasis,
  message,
  showActorName,
  onOpenActor,
  onOpenQuote,
  trailing,
}: OpportunityActivityLineProps) => {
  const { change } = sentence;

  // Below 24rem the change drops under the sentence, still right-aligned,
  // so the sentence keeps its width.
  return (
    <div className="@container">
      <div
        className={cn(
          "grid grid-cols-[34px_minmax(0,1fr)_auto] items-baseline gap-x-2 rounded-md px-2.5 py-1.5 text-sm leading-6 @[24rem]:grid-cols-[34px_minmax(9rem,1fr)_fit-content(40%)_auto]",
          emphasis === "external" && "bg-info/5",
          emphasis === "muted" && "opacity-70",
        )}
      >
        <div className="col-start-1 row-start-1 mt-px self-start">
          <ItemHistoryAvatars actor={actor} />
        </div>
        <p
          className={cn(
            "col-start-2 row-start-1 min-w-0 break-words",
            !change && "@[24rem]:col-span-2",
          )}
        >
          {showActorName ? (
            <>
              <ActorName
                name={actor.name}
                external={emphasis === "external"}
                onOpen={onOpenActor}
              />{" "}
            </>
          ) : null}
          {sentence.parts.map((part, index) => (
            <Fragment key={index}>
              {index > 0 ? " " : null}
              <SentencePart part={part} onOpenQuote={onOpenQuote} />
            </Fragment>
          ))}
          {actor.behalf && showActorName ? (
            <>
              {" "}
              <span className="text-muted-foreground">on behalf of</span>{" "}
              <span className="font-medium">{actor.behalf.name}</span>
            </>
          ) : null}
          {sentence.context ? (
            <>
              {" "}
              <span className="inline-block max-w-full truncate align-bottom text-muted-foreground">
                · {sentence.context}
              </span>
            </>
          ) : null}
          {trailing ? <> {trailing}</> : null}
        </p>
        {change ? (
          <div className="col-start-2 row-start-2 min-w-0 @[24rem]:col-start-3 @[24rem]:row-start-1 @[24rem]:pl-2">
            <ItemHistoryDetailPopover
              className="w-full cursor-default rounded-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              details={[change]}
              onSelectLine={() => undefined}
              canOpen={isCut}
            >
              <ItemHistoryChangeValues from={change.from} to={change.to} />
            </ItemHistoryDetailPopover>
          </div>
        ) : null}
        <span className="col-start-3 row-start-1 whitespace-nowrap text-xs tabular-nums text-muted-foreground @[24rem]:col-start-4">
          {time}
        </span>
        {sentence.preview && message ? (
          <div className="col-span-2 col-start-2 min-w-0 @[24rem]:col-span-3">
            <OpportunityActivityMessagePopover
              preview={sentence.preview}
              message={message}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

// The actor's name; for an agent with a thread, the button that opens it.
const ActorName = ({
  name,
  external,
  onOpen,
}: {
  name: string;
  external: boolean;
  onOpen: (() => void) | null;
}) => {
  const classes = cn("font-medium", external && "text-info-foreground");
  if (!onOpen) {
    return <span className={classes}>{name}</span>;
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        classes,
        "rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      {name}
    </button>
  );
};

const SentencePart = ({
  part,
  onOpenQuote,
}: {
  part: ActivitySentencePart;
  onOpenQuote: (quoteId: string) => void;
}) => {
  if (part.type === "verb") {
    return <span className="text-muted-foreground">{part.text}</span>;
  }
  if (part.type === "object") {
    return <span className="font-semibold">{part.text}</span>;
  }
  if (part.type === "person") {
    return <span className="font-medium">{part.text}</span>;
  }
  if (part.type === "count") {
    return (
      <span className="inline-block rounded bg-muted px-1.5 align-[1px] text-[11px] font-semibold leading-[18px] tabular-nums text-muted-foreground">
        {part.count}×
      </span>
    );
  }
  if (part.type === "chip") {
    return (
      <ItemHistoryDetailPopover
        className={CHIP_CLASSES}
        details={part.details}
        onSelectLine={() => undefined}
      >
        {part.label}
      </ItemHistoryDetailPopover>
    );
  }
  if (part.type === "download") {
    const { assetId } = part;
    return (
      <button
        type="button"
        className={CHIP_CLASSES}
        onClick={() => downloadAsset(assetId).catch(() => undefined)}
      >
        <Download aria-hidden className="relative top-0.5 h-3 w-3 shrink-0" />
        {part.label}
      </button>
    );
  }
  const { quoteId } = part;
  if (!quoteId) {
    return <span className="font-semibold">{part.label}</span>;
  }
  return (
    <button
      type="button"
      onClick={() => onOpenQuote(quoteId)}
      className={LINK_CLASSES}
    >
      {part.label}
    </button>
  );
};

// Whether a value in the cell is cut short by its ellipsis right now.
const isCut = (cell: HTMLElement) =>
  [...cell.querySelectorAll("span")].some(
    (span) => span.scrollWidth > span.clientWidth,
  );
